import { useEffect, useRef, useState } from 'react';
import { Pin, PinOff } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useScopePinStore } from '@/stores/scope-pin';
import { currentMonthStart } from '@/lib/fiscal-year';
import { MonthPicker } from '@/components/MonthPicker';

/** N ヶ月前の月初日 (TrendsPage.tsx 等の既存実装と同一)。 */
const monthsBackStart = (n: number): string => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const monthLabel = (month: string): string => {
  const [y, m] = month.split('-').map((s) => Number.parseInt(s, 10));
  return `${y}年${m}月`;
};

const monthLabelShort = (month: string): string => {
  const [, m] = month.split('-').map((s) => Number.parseInt(s, 10));
  return `${m}月`;
};

/** ヘッダーの参照期間固定トグル (5-1、docs/design/v05-dashboard-scope-pinning.md §0-2-3)。
 *  ノード軸 (useScopeStore.selectedNodeId) は既に常時グローバル永続化されているため対象外、
 *  本コンポーネントが新規に導入するのは月軸 (開始月・終了月ペア) の固定のみ。
 *  `NodeSwitcher`とは異なり `hierarchyManagementEnabled` ではなく `tenant_features.scope_pinning`
 *  (既定OFF) でガードする。パネルの開閉・フォーカストラップ・モバイルbottom sheet化は
 *  `NodeSwitcher.tsx` の実装パターンをそのまま踏襲する (新規に別パターンを増やさない)。 */
export function ScopePinToggle() {
  const tenantId = useAuthStore((s) => s.user?.tenant_id ?? null);
  const scopePinningEnabled = useAuthStore((s) => s.user?.tenant_features?.scope_pinning ?? false);
  const isPinned = useScopePinStore((s) => s.isPinned);
  const pinnedPeriodStart = useScopePinStore((s) => s.pinnedPeriodStart);
  const pinnedPeriodEnd = useScopePinStore((s) => s.pinnedPeriodEnd);
  const pin = useScopePinStore((s) => s.pin);
  const unpin = useScopePinStore((s) => s.unpin);

  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const startInputRef = useRef<HTMLButtonElement | null>(null);

  // 実装レビューHIGH (design-expert/QA): NodeSwitcherの検索欄は autoFocus で開時にフォーカスが
  // 移るが、本コンポーネントは移らなかった。パネルオープン時に開始月inputへフォーカスする。
  useEffect(() => {
    if (open) startInputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    // NodeSwitcher.tsx と同一のフォーカストラップ・Escapeパターン
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input, [tabindex]:not([tabindex="-1"])'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      triggerRef.current?.focus();
    };
  }, [open]);

  if (!scopePinningEnabled || !tenantId) return null;

  const openPanel = () => {
    setDraftStart(pinnedPeriodStart ?? monthsBackStart(12));
    setDraftEnd(pinnedPeriodEnd ?? currentMonthStart());
    setOpen(true);
  };

  const isDraftValid = Boolean(draftStart) && Boolean(draftEnd) && draftStart <= draftEnd;

  const handleConfirm = () => {
    if (!isDraftValid) return;
    pin(tenantId, draftStart, draftEnd);
    setOpen(false);
  };

  const fullLabel = isPinned && pinnedPeriodStart && pinnedPeriodEnd
    ? `${monthLabel(pinnedPeriodStart)}〜${monthLabel(pinnedPeriodEnd)}に固定中`
    : null;
  const shortLabel = isPinned && pinnedPeriodStart && pinnedPeriodEnd
    ? `${monthLabelShort(pinnedPeriodStart)}〜${monthLabelShort(pinnedPeriodEnd)}`
    : null;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={openPanel}
        className="flex min-h-[44px] items-center gap-1.5 px-2 py-1.5 rounded text-sm text-chrome-text-sub hover:bg-chrome-raised hover:text-white transition max-w-[40vw] sm:max-w-xs"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={fullLabel ? `${fullLabel}（クリックして変更）` : '参照期間を固定'}
        title={fullLabel ?? undefined}
      >
        <Pin size={16} className="shrink-0" />
        {isPinned ? (
          <>
            <span className="truncate hidden sm:inline">{fullLabel}</span>
            <span className="truncate sm:hidden">{shortLabel}</span>
          </>
        ) : (
          <span className="truncate hidden sm:inline">固定</span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="参照期間の固定"
          // 実装レビューCRITICAL (design-expert): NodeSwitcher.tsxから踏襲した`sm:overflow-hidden`は
          // ツリー表示の内部スクロール用の指定であり、本コンポーネントには不要などころか、
          // 開始月/終了月のMonthPickerが展開するカレンダー(position:absolute)をデスクトップ幅で
          // クリップし選択不能にしていた。ネストしたポップアップを外側へはみ出させる必要があるため外す。
          className="fixed inset-x-0 bottom-0 z-[45] max-h-[70vh] w-full overflow-y-auto rounded-t bg-white border-t border-neutral-line shadow-lg sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:top-full sm:mt-1 sm:w-72 sm:max-w-[90vw] sm:max-h-none sm:rounded sm:border"
        >
          <div className="p-3 space-y-3">
            <div>
              <label className="block text-xs text-neutral-sub mb-1" htmlFor="scope-pin-start">開始月</label>
              <MonthPicker
                ref={startInputRef}
                id="scope-pin-start"
                aria-label="開始月"
                value={draftStart}
                onChange={(v) => setDraftStart(`${v}-01`)}
                className="w-full min-h-[44px] px-2 py-1.5 text-sm border border-neutral-line rounded focus-ring"
              />
            </div>
            <div>
              <label className="block text-xs text-neutral-sub mb-1" htmlFor="scope-pin-end">終了月</label>
              <MonthPicker
                id="scope-pin-end"
                aria-label="終了月"
                value={draftEnd}
                onChange={(v) => setDraftEnd(`${v}-01`)}
                className="w-full min-h-[44px] px-2 py-1.5 text-sm border border-neutral-line rounded focus-ring"
              />
            </div>
            {!isDraftValid && draftStart && draftEnd && (
              <p className="text-xs text-danger-text">開始月は終了月以前を指定してください</p>
            )}
            <div className="flex items-center justify-between gap-2 pt-1">
              {isPinned && (
                <button
                  type="button"
                  onClick={() => { unpin(tenantId); setOpen(false); }}
                  className="inline-flex items-center gap-1 min-h-[44px] text-sm text-neutral-sub hover:text-neutral-text hover:underline"
                >
                  <PinOff size={14} />
                  固定を解除
                </button>
              )}
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!isDraftValid}
                className="ml-auto px-3 min-h-[44px] text-sm rounded bg-brand-red text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
              >
                {isPinned ? '更新する' : '固定する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
